import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { SlideshowData, SlideshowTimelineItem } from '../../types';
import './RideSlideshowModal.css';

interface RideSlideshowModalProps {
  rideId: number;
  onClose: () => void;
}

const AudioPlayer: React.FC<{ audioUrl: string; durationS: number }> = ({ audioUrl, durationS }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const toggle = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.ontimeupdate = () => {
        if (audioRef.current) {
          setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
        }
      };
      audioRef.current.onended = () => { setPlaying(false); setProgress(0); };
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  }, [audioUrl, playing]);

  return (
    <div className="slideshow-audio-player">
      <button className="slideshow-audio-play-btn" onClick={toggle}>
        {playing ? '\u23F8' : '\u25B6'}
      </button>
      <div className="slideshow-audio-progress">
        <div className="slideshow-audio-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <span className="slideshow-audio-duration">{formatTime(durationS)}</span>
    </div>
  );
};

const RideSlideshowModal: React.FC<RideSlideshowModalProps> = ({ rideId, onClose }) => {
  const { t } = useLanguage();
  const [slideshow, setSlideshow] = useState<SlideshowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSlideshow = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/api/feed/ride-slideshow/${rideId}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to load slideshow');
        const data = await res.json();
        setSlideshow(data.slideshow);
      } catch {
        setError('Failed to load ride slideshow');
      } finally {
        setLoading(false);
      }
    };
    fetchSlideshow();
  }, [rideId]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const formatDistance = (km: number | null) => {
    if (!km) return '0 km';
    return `${km.toFixed(1)} km`;
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return '0h';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const formatRelativeTime = (minutes: number | null) => {
    if (minutes === null || minutes === undefined) return '';
    if (minutes < 0) return '';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `+${h}h${m > 0 ? ` ${m}m` : ''}`;
    return `+${m}m`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderTimelineItem = (item: SlideshowTimelineItem, index: number) => {
    return (
      <div key={`${item.type}-${item.user_id}-${index}`} className="slideshow-timeline-item">
        <div className="slideshow-timeline-dot" />
        <div className="slideshow-timeline-content">
          <div className="slideshow-item-header">
            {item.profile_picture ? (
              <img src={item.profile_picture} alt="" className="slideshow-item-avatar" />
            ) : (
              <div className="slideshow-item-avatar-placeholder">
                {item.user_name?.[0] || '?'}
              </div>
            )}
            <div className="slideshow-item-user-info">
              <span className="slideshow-item-user-name">{item.user_name}</span>
              {item.relative_minutes !== null && (
                <span className="slideshow-item-time">{formatRelativeTime(item.relative_minutes)}</span>
              )}
            </div>
          </div>

          {item.type === 'voice' && (
            <div className="slideshow-item-body">
              {item.audio_url && item.audio_duration_s && (
                <AudioPlayer audioUrl={item.audio_url} durationS={item.audio_duration_s} />
              )}
              {item.content && (
                <p className="slideshow-item-transcript">{item.content}</p>
              )}
            </div>
          )}

          {item.type === 'photo' && (
            <div className="slideshow-item-body">
              {item.image_url && (
                <img src={item.image_url} alt="" className="slideshow-item-image" loading="lazy" />
              )}
              {item.content && (
                <p className="slideshow-item-text">{item.content}</p>
              )}
            </div>
          )}

          {item.type === 'text' && item.content && (
            <div className="slideshow-item-body">
              <p className="slideshow-item-text">{item.content}</p>
            </div>
          )}

          {item.type === 'checkin' && (
            <div className="slideshow-item-body">
              <p className="slideshow-item-text">{item.content || 'Checked in'}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="slideshow-modal-overlay" onClick={onClose}>
      <div className="slideshow-modal" onClick={e => e.stopPropagation()}>
        <button className="slideshow-modal-close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {loading && (
          <div className="slideshow-modal-loading">
            <div className="slideshow-loading-spinner" />
            <p>{t('common.loading') || 'Loading...'}</p>
          </div>
        )}

        {error && (
          <div className="slideshow-modal-error">
            <p>{error}</p>
            <button onClick={onClose}>{t('common.close') || 'Close'}</button>
          </div>
        )}

        {slideshow && !loading && !error && (
          <div className="slideshow-modal-content">
            {/* Header */}
            <div className="slideshow-header-section">
              {slideshow.highlight_image_url ? (
                <img src={slideshow.highlight_image_url} alt="" className="slideshow-header-image" />
              ) : (
                <div className="slideshow-header-fallback" />
              )}
              <div className="slideshow-header-overlay">
                <h2 className="slideshow-header-title">{slideshow.title}</h2>
                {slideshow.ride_date && (
                  <p className="slideshow-header-date">{formatDate(slideshow.ride_date)}</p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="slideshow-summary-stats">
              {slideshow.total_distance_km !== null && (
                <div className="slideshow-stat">
                  <span className="slideshow-stat-value">{formatDistance(slideshow.total_distance_km)}</span>
                  <span className="slideshow-stat-label">Distance</span>
                </div>
              )}
              {slideshow.total_elevation_m !== null && (
                <div className="slideshow-stat">
                  <span className="slideshow-stat-value">{Math.round(slideshow.total_elevation_m)} m</span>
                  <span className="slideshow-stat-label">Elevation</span>
                </div>
              )}
              {slideshow.duration_minutes !== null && (
                <div className="slideshow-stat">
                  <span className="slideshow-stat-value">{formatDuration(slideshow.duration_minutes)}</span>
                  <span className="slideshow-stat-label">Duration</span>
                </div>
              )}
              <div className="slideshow-stat">
                <span className="slideshow-stat-value">{slideshow.participant_count}</span>
                <span className="slideshow-stat-label">Riders</span>
              </div>
              <div className="slideshow-stat">
                <span className="slideshow-stat-value">{slideshow.photo_count}</span>
                <span className="slideshow-stat-label">Photos</span>
              </div>
              {slideshow.voice_note_count > 0 && (
                <div className="slideshow-stat">
                  <span className="slideshow-stat-value">{slideshow.voice_note_count}</span>
                  <span className="slideshow-stat-label">Voice Notes</span>
                </div>
              )}
            </div>

            {/* Timeline */}
            {slideshow.timeline.length > 0 && (
              <div className="slideshow-timeline">
                <h3 className="slideshow-timeline-title">
                  {slideshow.timeline.length} moments
                </h3>
                {slideshow.timeline.map(renderTimelineItem)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RideSlideshowModal;
